import React, { useState } from 'react';
import { Box, Collapse, Link, Typography } from '@mui/material';
import { Bone } from '../../../../../../components/ui/DashboardSkeletons';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { API } from '../../../../../../api';
import { getUserDetails } from '../../../../../../utils/orgName';
import RenderHTML from '../../../../../../components/RenderHTML';
import { elliottTokens as t } from '../tokens';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';
import { messageBodySx } from './ElliottMessage';

export interface LessonMaterial {
  id: string;
  title: string;
  thumbnail?: string;
  readTime?: string;
}

interface LessonMaterialCardProps {
  material: LessonMaterial;
  /** Shown when the article has no cover of its own (org logo, then Dosen mark). */
  fallbackThumbnail: string;
  /** Escape hatch — opens the article in the Library view. */
  onOpenInLibrary: () => void;
}

// Same accordion behaviour as the other Elliott blocks (case studies): the
// card expands inline and shows the material's content — no redirect. The
// article body is fetched lazily on first expand; "Open in Library" stays
// as a secondary action inside the expanded body.
const LessonMaterialCard: React.FC<LessonMaterialCardProps> = ({
  material,
  fallbackThumbnail,
  onOpenInLibrary
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [article, setArticle] = useState<any>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !article && !loading) {
      setLoading(true);
      const { orgId, location } = getUserDetails();
      API.getAllArticleById({
        orgId,
        articleId: material.id,
        isContentSettings: false,
        articleLocation: location || ''
      })
        .then((res: any) => {
          if (res?.status === 200) setArticle(res.data.article);
          else setLoadFailed(true);
        })
        .catch(() => setLoadFailed(true))
        .finally(() => setLoading(false));
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        px: '16px',
        py: '12px',
        borderRadius: '8px',
        background: 'linear-gradient(180deg, rgba(247,148,32,0.1) 0%, rgba(241,90,38,0.1) 100%)'
      }}
    >
      {/* Header — click to expand/collapse (accordion, like case studies). */}
      <Box
        onClick={toggle}
        sx={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
      >
        <Box
          component="img"
          src={material.thumbnail || fallbackThumbnail}
          alt={material.title}
          onError={(e: any) => {
            e.target.src = fallbackThumbnail;
          }}
          sx={{ width: 56, height: 40, borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              fontSize: 14,
              fontWeight: 700,
              lineHeight: '24px',
              color: t.textPrimary,
              // Phone: the whole title, over two lines when it needs them.
              [MOBILE_SX]: { whiteSpace: 'normal', lineHeight: '20px' }
            }}
          >
            {material.title}
          </Typography>
          {material.readTime && (
            <Typography sx={{ fontSize: 12, lineHeight: '16px', color: t.textSecondary }}>
              {material.readTime}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', color: t.textSecondary, flexShrink: 0 }}>
          {expanded ? <LuChevronUp size={20} /> : <LuChevronDown size={20} />}
        </Box>
      </Box>

      <Collapse in={expanded} timeout={250} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Box sx={{ height: '1px', width: '100%', bgcolor: 'rgba(255,255,255,0.45)' }} />

          {loading && (
            <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: '8px', py: '6px' }}>
              <Bone height={16} width="55%" />
              <Bone height={14} width="90%" />
              <Bone height={14} width="75%" />
            </Box>
          )}

          {!loading && loadFailed && (
            <Typography sx={{ ...messageBodySx, color: t.textSecondary }}>
              This material couldn't be loaded here. You can{' '}
              <Link
                onClick={onOpenInLibrary}
                sx={{ color: t.brandLight, cursor: 'pointer', textDecorationColor: t.brandLight }}
              >
                open it in Resources
              </Link>
              .
            </Typography>
          )}

          {!loading && article && (
            <>
              {article.type === 'Video' && article.fileUrl && (
                <Box component="video" controls src={article.fileUrl} sx={{ width: '100%', borderRadius: '8px' }} />
              )}
              {article.type === 'Audio' && article.fileUrl && (
                <Box component="audio" controls sx={{ width: '100%' }}>
                  <source src={article.fileUrl} />
                </Box>
              )}
              {article.type === 'Text' && typeof article.fileUrl === 'string' && article.fileUrl.includes('http') && (
                <Link
                  onClick={() => window.open(article.fileUrl, '_blank')}
                  sx={{ color: t.brandLight, fontSize: 14, cursor: 'pointer', fontFamily: 'Inter' }}
                >
                  {article.articleName || material.title}
                </Link>
              )}
              {article.articleDescription && (
                <Box sx={{ ...messageBodySx, '& img': { maxWidth: '100%', borderRadius: '8px' } }}>
                  <RenderHTML htmlContent={article.articleDescription} />
                </Box>
              )}
              <Link
                onClick={onOpenInLibrary}
                sx={{
                  alignSelf: 'flex-start',
                  color: t.textSecondary,
                  fontSize: 12.5,
                  cursor: 'pointer',
                  textDecorationColor: t.borderContrast,
                  '&:hover': { color: t.textPrimary }
                }}
              >
                Open in Resources
              </Link>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default LessonMaterialCard;
